import { createClient } from '@supabase/supabase-js';
import { parsePublicPageHandle } from '@/utils/publicPage';

/**
 * Lien public détecté dans l'URL au chargement de la page.
 *
 * C'est la pièce maîtresse de l'accès sans compte : quand l'URL est
 * `/p/<slug>-<token>`, le client Supabase porte ces deux valeurs dans des
 * en-têtes HTTP. Les policies `*_public_link_view` en base les résolvent en une
 * entreprise et ouvrent exactement le même périmètre de lecture qu'un client
 * connecté. Tous les écrans existants fonctionnent donc sans modification.
 *
 * La lecture se fait une seule fois, à l'initialisation du module : un visiteur
 * arrive toujours sur cette URL par un chargement complet de page.
 */
function readPublicLinkFromUrl(): { slug: string; token: string; handle: string } | null {
  if (typeof window === 'undefined') return null;

  const match = window.location.pathname.match(/^\/p\/([^/?#]+)/);
  if (!match) return null;

  const handle = decodeURIComponent(match[1]);
  const parsed = parsePublicPageHandle(handle);
  return parsed ? { ...parsed, handle } : null;
}

export const publicLink = readPublicLinkFromUrl();

/** true quand la page est ouverte via un lien public, sans compte */
export const isPublicLinkSession = publicLink !== null;

export const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY,
  {
    auth: {
      // Un visiteur par lien ne doit laisser aucune session derrière lui,
      // et ne doit pas hériter de celle d'un admin ouvrant le lien pour tester.
      persistSession: !isPublicLinkSession,
      autoRefreshToken: !isPublicLinkSession,
    },
    ...(publicLink
      ? {
          global: {
            headers: {
              'x-public-slug': publicLink.slug,
              'x-public-token': publicLink.token,
            },
          },
        }
      : {}),
  }
);
